<template>
  <div class="animated fadeIn pvp-usage-view">
    <div class="usage-container">
      <b-card no-body class="border-0 shadow-sm mb-3 bg-dark text-white usage-toolbar-card">
        <b-card-body class="p-4">
          <div class="usage-toolbar">
            <div class="usage-title">
              <div class="usage-title-icon">
                <i class="fa fa-bar-chart"></i>
              </div>
              <div>
                <h4 class="mb-1">Usage Stats</h4>
                <div class="text-muted small">Monthly PvP Pokemon usage by most used.</div>
              </div>
            </div>

            <div class="usage-controls">
              <div class="control-block">
                <div class="control-label">Server</div>
                <div class="btn-group btn-group-sm">
                  <button
                    v-for="server in activeServers"
                    :key="server"
                    type="button"
                    class="btn"
                    :class="activeServer === server ? 'btn-info active' : 'btn-outline-secondary'"
                    @click="switchServer(server)"
                  >
                    {{ capitalize(server) }}
                  </button>
                </div>
              </div>

              <div class="control-block month-control">
                <div class="control-label">Month</div>
                <b-form-select
                  v-model="selectedMonthKey"
                  :options="monthOptions"
                  size="sm"
                  @change="expandedKey = null"
                ></b-form-select>
              </div>

              <div class="control-block search-control">
                <div class="control-label">Search</div>
                <b-input-group size="sm" class="search-box-group">
                  <b-input-group-prepend class="search-box-prepend">
                    <i class="fa fa-search"></i>
                  </b-input-group-prepend>
                  <b-form-input
                    v-model="quickFilter"
                    placeholder="Pokemon name..."
                    class="search-box-input"
                  />
                  <b-input-group-append v-if="quickFilter">
                    <b-button variant="outline-secondary" class="search-clear" @click="quickFilter = ''">
                      <i class="fa fa-times small"></i>
                    </b-button>
                  </b-input-group-append>
                </b-input-group>
              </div>

              <b-button variant="outline-info" size="sm" :disabled="loading" @click="refresh">
                <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
              </b-button>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <b-row class="mb-3">
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">Total matches</div>
            <div class="meta-value">{{ selectedMonth ? selectedMonth.total_matches : 0 }}</div>
          </div>
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">Last update</div>
            <div class="meta-value meta-date">{{ selectedMonth ? formatDate(selectedMonth.last_updated) : '-' }}</div>
          </div>
        </b-col>
        <b-col cols="12" md="4" class="mb-2">
          <div class="meta-tile">
            <div class="meta-label">{{ selectedMonth && selectedMonth.is_current ? 'Next update' : 'Archive status' }}</div>
            <div class="meta-value meta-date">
              <span v-if="selectedMonth && selectedMonth.is_current">{{ nextUpdateCountdown }}</span>
              <span v-else>Stored snapshot</span>
            </div>
          </div>
        </b-col>
      </b-row>

      <b-card no-body class="border-0 shadow-sm bg-dark text-white usage-table-card">
        <div v-if="loading" class="loading-overlay">
          <b-spinner label="Loading..." variant="info"></b-spinner>
          <div class="text-muted mt-2">Loading usage stats...</div>
        </div>

        <div v-if="!loading && !filteredPokemon.length" class="empty-state">
          <i class="fa fa-bar-chart fa-3x mb-3"></i>
          <div class="h5">No usage data found.</div>
        </div>

        <div v-else class="table-responsive usage-table-wrapper">
          <table class="table table-dark table-hover mb-0 usage-table">
            <thead>
              <tr>
                <th class="rank-col">Rank</th>
                <th>Pokemon</th>
                <th class="text-right">Count</th>
                <th class="text-right">Percentage</th>
                <th class="text-right">Win rate</th>
              </tr>
            </thead>
            <tbody v-for="(pokemon, index) in filteredPokemon" :key="rowKey(pokemon)">
              <tr :class="{ 'row-expanded': isExpanded(pokemon) }">
                <td class="rank-col">#{{ index + 1 }}</td>
                <td>
                  <button type="button" class="pokemon-link" @click="togglePokemon(pokemon)">
                    <span class="pokemon-icon">
                      <img
                        :src="getPokemonSpriteUrl(pokemon)"
                        :alt="pokemon.Name"
                        :data-fallback-id="getPokemonFallbackId(getPokemonDex(pokemon))"
                        @error="handleIconError"
                      />
                      <i class="fa fa-question" aria-hidden="true"></i>
                    </span>
                    <span>{{ pokemon.Name || 'Unknown' }}</span>
                    <i class="fa ml-2" :class="isExpanded(pokemon) ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                  </button>
                </td>
                <td class="text-right font-weight-bold">{{ statCount(pokemon) }}</td>
                <td class="text-right">{{ formatPercent(pokemon.Percentage) }}</td>
                <td class="text-right">
                  <span class="win-rate" :class="winRateClass(pokemon.WinRate)">
                    {{ formatPercent(pokemon.WinRate) }}
                  </span>
                </td>
              </tr>
              <tr v-if="isExpanded(pokemon)" class="detail-row">
                <td colspan="5">
                  <div class="detail-panel">
                    <div class="detail-header">
                      <span class="detail-pokemon-icon">
                        <img
                          :src="getPokemonSpriteUrl(pokemon)"
                          :alt="pokemon.Name"
                          :data-fallback-id="getPokemonFallbackId(getPokemonDex(pokemon))"
                          @error="handleIconError"
                        />
                        <i class="fa fa-question" aria-hidden="true"></i>
                      </span>
                      <div>
                        <div class="detail-name">{{ pokemon.Name }}</div>
                        <div class="detail-subtitle">
                          {{ statCount(pokemon) }} uses, {{ formatPercent(pokemon.Percentage) }} usage,
                          {{ formatPercent(pokemon.WinRate) }} win rate,
                          {{ formatPercent(pokemon['Opener%']) }} opener
                        </div>
                      </div>
                    </div>

                    <b-row>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-leaf mr-2"></i>Natures</div>
                          <div v-if="limitedRows(pokemon.Natures).length">
                            <div v-for="row in limitedRows(pokemon.Natures)" :key="`nature-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">{{ formatPercent(row['Use%']) }} / {{ formatPercent(row['Win%']) }}</span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-bolt mr-2"></i>Abilities</div>
                          <div v-if="limitedRows(pokemon.Abilities).length">
                            <div v-for="row in limitedRows(pokemon.Abilities)" :key="`ability-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">{{ formatPercent(row['Use%']) }} / {{ formatPercent(row['Win%']) }}</span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-crosshairs mr-2"></i>Moves</div>
                          <div v-if="limitedRows(pokemon.Moves).length">
                            <div v-for="row in limitedRows(pokemon.Moves)" :key="`move-${row.Name}`" class="stat-line">
                              <span class="stat-name">{{ row.Name || 'None' }}</span>
                              <span class="stat-values">{{ formatPercent(row['Use%']) }} / {{ formatPercent(row['Win%']) }}</span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-gift mr-2"></i>Held Items</div>
                          <div v-if="limitedRows(pokemon.HeldItems).length">
                            <div v-for="item in limitedRows(pokemon.HeldItems)" :key="`item-${item.ID}-${item.Name}`" class="stat-line">
                              <span class="stat-name with-icon">
                                <span v-if="item.Name && item.Name !== 'None'" class="item-icon">
                                  <img :src="getItemSpriteUrl(item)" :alt="item.Name" @error="handleIconError" />
                                  <i class="fa fa-question" aria-hidden="true"></i>
                                </span>
                                {{ item.Name || 'None' }}
                              </span>
                              <span class="stat-values">{{ formatPercent(item['Use%']) }} / {{ formatPercent(item['Win%']) }}</span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-users mr-2"></i>Teammates</div>
                          <div v-if="limitedRows(pokemon.Teammates).length">
                            <div v-for="mate in limitedRows(pokemon.Teammates)" :key="`mate-${mate.DexID}-${mate.Name}`" class="stat-line">
                              <span class="stat-name with-icon">
                                <span class="pokemon-icon mini">
                                  <img
                                    :src="getPokemonSpriteUrl(mate.DexID)"
                                    :alt="mate.Name"
                                    :data-fallback-id="getPokemonFallbackId(mate.DexID)"
                                    @error="handleIconError"
                                  />
                                  <i class="fa fa-question" aria-hidden="true"></i>
                                </span>
                                {{ mate.Name }}
                              </span>
                              <span class="stat-values">{{ formatPercent(mate['Use%']) }} / {{ formatPercent(mate['Win%']) }}</span>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                      <b-col cols="12" lg="4" class="mb-3">
                        <div class="stat-block">
                          <div class="stat-block-title"><i class="fa fa-list mr-2"></i>Move Sets</div>
                          <div v-if="limitedRows(pokemon.MoveSets).length">
                            <div v-for="(set, setIndex) in limitedRows(pokemon.MoveSets)" :key="`set-${setIndex}`" class="moveset-line">
                              <div class="moveset-moves">
                                <span v-for="move in set.MoveSet" :key="`${setIndex}-${move}`" class="move-chip">{{ move }}</span>
                              </div>
                              <div class="moveset-stats">{{ formatPercent(set['Use%']) }} use / {{ formatPercent(set['Win%']) }} win</div>
                            </div>
                          </div>
                          <div v-else class="stat-empty">No data</div>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import Servers from '@/_servers'
import Monsters from '@/views/pokemon/_monsters'

export default {
  name: 'PvpUsageStats',
  data() {
    return {
      activeServer: 'gold',
      selectedMonthKey: '',
      quickFilter: '',
      expandedKey: null,
      loading: false,
      tick: Date.now(),
      timer: null,
      servers: Servers,
      monsters: Object.freeze(Monsters)
    }
  },
  computed: {
    ...mapGetters(['pvpUsageStats', 'pvpUsageStatsReceivedAt']),
    activeServers() {
      return Object.keys(this.servers).filter(server => !this.servers[server].readonly)
    },
    monthOptions() {
      return this.months.map(month => ({
        value: month.key,
        text: month.is_current ? `${month.label} (current)` : month.label
      }))
    },
    months() {
      return this.pvpUsageStats && this.pvpUsageStats.months ? this.pvpUsageStats.months : []
    },
    selectedMonth() {
      return this.months.find(month => month.key === this.selectedMonthKey) || this.months[0] || null
    },
    sortedPokemon() {
      const rows = this.selectedMonth && this.selectedMonth.pokemon ? this.selectedMonth.pokemon : []
      return rows.slice().sort((a, b) => {
        const countDiff = this.statCount(b) - this.statCount(a)
        if (countDiff !== 0) return countDiff
        return String(a.Name || '').localeCompare(String(b.Name || ''))
      })
    },
    filteredPokemon() {
      const query = this.quickFilter.trim().toLowerCase()
      if (!query) return this.sortedPokemon
      return this.sortedPokemon.filter(row => String(row.Name || '').toLowerCase().includes(query))
    },
    nextUpdateCountdown() {
      const tick = this.tick
      if (tick < 0) return '-'
      if (!this.selectedMonth || !this.selectedMonth.next_update) return '-'
      const diff = moment.utc(this.selectedMonth.next_update).valueOf() - Date.now()
      if (diff <= 0) return 'Refresh due'
      const totalSeconds = Math.floor(diff / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      return `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`
    }
  },
  watch: {
    pvpUsageStatsReceivedAt() {
      this.loading = false
      if (!this.months.some(month => month.key === this.selectedMonthKey)) {
        this.selectedMonthKey = this.months.length ? this.months[0].key : ''
      }
      this.expandedKey = null
    }
  },
  created() {
    if (!this.activeServers.includes(this.activeServer) && this.activeServers.length) {
      this.activeServer = this.activeServers[0]
    }
    this.refresh()
  },
  mounted() {
    this.timer = setInterval(() => {
      this.tick = Date.now()
    }, 1000)
  },
  beforeDestroy() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    refresh() {
      if (!this.activeServer) return
      this.loading = true
      this.emit('getPvpUsageStats', { server: this.activeServer })
    },
    switchServer(server) {
      if (server === this.activeServer) return
      this.activeServer = server
      this.selectedMonthKey = ''
      this.quickFilter = ''
      this.expandedKey = null
      this.refresh()
    },
    capitalize(value) {
      return String(value || '').charAt(0).toUpperCase() + String(value || '').slice(1)
    },
    pad(value) {
      return String(value).padStart(2, '0')
    },
    rowKey(pokemon) {
      return `${pokemon.DexID || 0}-${pokemon.Name || 'unknown'}`
    },
    isExpanded(pokemon) {
      return this.expandedKey === this.rowKey(pokemon)
    },
    togglePokemon(pokemon) {
      const key = this.rowKey(pokemon)
      this.expandedKey = this.expandedKey === key ? null : key
    },
    statCount(pokemon) {
      return Number(pokemon.Count || pokemon.Matches || 0)
    },
    formatPercent(value) {
      const numeric = Number(value || 0)
      return `${numeric.toFixed(2)}%`
    },
    formatDate(value) {
      if (!value) return '-'
      return moment.utc(value).local().format('YYYY-MM-DD HH:mm:ss')
    },
    winRateClass(value) {
      const numeric = Number(value || 0)
      if (numeric >= 55) return 'win-rate-high'
      if (numeric <= 45) return 'win-rate-low'
      return ''
    },
    limitedRows(rows) {
      return (rows || []).slice(0, 8)
    },
    getPokemonDex(value) {
      if (typeof value === 'number') return value
      if (value && value.DexID) return Number(value.DexID)
      const name = value && value.Name ? String(value.Name).toLowerCase() : ''
      if (!name) return 0
      const found = this.monsters.find(monster => String(monster.Name || '').toLowerCase() === name)
      return found ? Number(found.ID) : 0
    },
    getPokemonSpriteUrl(value) {
      const dex = this.getPokemonDex(value)
      if (!dex || Number.isNaN(dex)) return ''
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${dex}.png`
    },
    getPokemonFallbackId(id) {
      const numeric = Number(id)
      if (!numeric || Number.isNaN(numeric)) return ''
      const current = this.monsters.find(monster => Number(monster.ID) === numeric)
      if (!current || !current.Name) return ''
      const baseName = this.normalizePokemonName(current.Name)
      if (!baseName || baseName === current.Name) return ''
      const base = this.monsters.find(monster => String(monster.Name || '').toLowerCase() === baseName.toLowerCase())
      if (!base || Number(base.ID) === numeric) return ''
      return base.ID
    },
    normalizePokemonName(name) {
      const raw = String(name || '')
      if (raw.startsWith('Rotom-') || raw.startsWith('Rotom ')) {
        return 'Rotom'
      }
      return raw
        .replace(/\b(Alola|Alolan|Galarian|Hisuian|Paldean|Mega|Primal|Origin|Therian|Incarnate|Forme?|Mode|Sky|Midnight|Dusk|Dawn|Ultra|Gigantamax|Gmax|Fan|Wash|Heat|Frost|Mow)\b/gi, '')
        .replace(/\b(Attack|Defense|Speed|Complete|Ash|Black|White)\b/gi, '')
        .replace(/[-:]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    },
    getItemSpriteUrl(item) {
      const name = item && item.Name ? item.Name : ''
      if (!name || name === 'None') return ''
      const slug = String(name)
        .toLowerCase()
        .replace(/'/g, '')
        .replace(/\./g, '')
        .replace(/:/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim()
      return `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${slug}.png`
    },
    handleIconError(event) {
      if (!event || !event.target) return
      const target = event.target
      const fallbackId = target.dataset ? target.dataset.fallbackId : ''
      if (fallbackId) {
        target.src = this.getPokemonSpriteUrl(Number(fallbackId))
        target.dataset.fallbackId = ''
        return
      }
      target.style.display = 'none'
    }
  }
}
</script>

<style scoped>
.usage-container {
  max-width: 1500px;
  margin: 0 auto;
}

.usage-toolbar-card,
.usage-table-card {
  border-radius: 8px;
}

.usage-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.usage-title {
  display: flex;
  align-items: center;
  gap: 12px;
}

.usage-title-icon {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(32, 168, 216, 0.15);
  color: #8bdcf4;
  font-size: 20px;
}

.usage-controls {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.control-block {
  min-width: 140px;
}

.month-control {
  min-width: 190px;
}

.search-control {
  min-width: 240px;
}

.control-label,
.meta-label {
  font-size: 0.68rem;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 700;
  margin-bottom: 4px;
}

.search-box-group {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
}

.search-box-prepend {
  background: #12161b;
  border: 0;
  color: rgba(255, 255, 255, 0.55);
  padding: 0 12px;
  display: flex;
  align-items: center;
}

.search-box-input {
  background: #12161b;
  border: 0;
  color: #fff;
  font-weight: 600;
}

.search-clear {
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.08);
  background: #12161b;
  color: rgba(255, 255, 255, 0.6);
}

.meta-tile {
  background: #23282c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 14px 16px;
  min-height: 76px;
}

.meta-value {
  font-size: 1.35rem;
  color: #fff;
  font-weight: 700;
}

.meta-date {
  font-size: 1rem;
  line-height: 1.5;
}

.usage-table-card {
  position: relative;
  min-height: 280px;
  overflow: hidden;
}

.usage-table-wrapper {
  max-height: calc(100vh - 260px);
  min-height: 280px;
}

.usage-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #1f2327;
  border-top: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.68);
  font-size: 0.72rem;
  text-transform: uppercase;
}

.usage-table tbody + tbody {
  border-top: 0;
}

.usage-table td {
  vertical-align: middle;
  border-top-color: rgba(255, 255, 255, 0.05);
}

.rank-col {
  width: 90px;
  color: rgba(255, 255, 255, 0.55);
  font-weight: 700;
}

.pokemon-link {
  border: 0;
  background: transparent;
  color: #fff;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
}

.pokemon-link:hover {
  color: #8bdcf4;
}

.pokemon-icon,
.item-icon,
.detail-pokemon-icon {
  position: relative;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.06);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}

.pokemon-icon.mini {
  width: 24px;
  height: 24px;
  border-radius: 6px;
}

.pokemon-icon img,
.item-icon img,
.detail-pokemon-icon img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  position: relative;
  z-index: 1;
}

.pokemon-icon i,
.item-icon i,
.detail-pokemon-icon i {
  display: none;
  color: rgba(255, 255, 255, 0.45);
}

.pokemon-icon img[style*="display: none"] + i,
.item-icon img[style*="display: none"] + i,
.detail-pokemon-icon img[style*="display: none"] + i {
  display: inline-block;
}

.win-rate {
  font-weight: 700;
}

.win-rate-high {
  color: #4dbd74;
}

.win-rate-low {
  color: #f86c6b;
}

.row-expanded {
  background: rgba(32, 168, 216, 0.08);
}

.detail-row td {
  padding: 0;
  background: #181c20;
}

.detail-panel {
  padding: 18px;
  border-top: 1px solid rgba(32, 168, 216, 0.25);
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.detail-pokemon-icon {
  width: 56px;
  height: 56px;
}

.detail-name {
  font-size: 1.2rem;
  font-weight: 800;
}

.detail-subtitle {
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.86rem;
}

.stat-block {
  background: #23282c;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 12px;
  height: 100%;
}

.stat-block-title {
  color: #8bdcf4;
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  margin-bottom: 10px;
}

.stat-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 6px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.stat-line:first-child {
  border-top: 0;
}

.stat-name {
  color: #fff;
  font-weight: 600;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stat-name.with-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.stat-values {
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.78rem;
  white-space: nowrap;
}

.stat-empty {
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.85rem;
}

.moveset-line {
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.moveset-line:first-child {
  border-top: 0;
}

.moveset-moves {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-bottom: 4px;
}

.move-chip {
  border-radius: 999px;
  background: rgba(32, 168, 216, 0.15);
  color: #ccefff;
  padding: 2px 8px;
  font-size: 0.72rem;
  font-weight: 700;
}

.moveset-stats {
  color: rgba(255, 255, 255, 0.62);
  font-size: 0.78rem;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(10, 12, 15, 0.72);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.empty-state {
  min-height: 280px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.42);
}

@media (max-width: 768px) {
  .usage-controls,
  .usage-toolbar {
    width: 100%;
  }

  .control-block,
  .month-control,
  .search-control {
    width: 100%;
  }

  .usage-table-wrapper {
    max-height: none;
  }
}
</style>
